import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { BasicTitle, BiggerSubtitle } from '@genially/design-system';

const rotateInVer = keyframes({
  '0%': {
    transform: 'rotateY(-360deg)',
    opacity: 0,
  },
  '100%': {
    transform: 'rotateY(0deg)',
    opacity: 1,
  },
});

const trackingInExpanfFwd = keyframes({
  '0%': {
    letterSpacing: '-0.5em',
    transform: 'translateZ(-700px)',
    opacity: 0,
  },
  '40%': {
    opacity: 0.6,
  },
  '100%': {
    transform: 'translateZ(0)',
    opacity: 1,
  },
});

const slideInTop = keyframes({
  '0%': {
    transform: 'translateY(-100%)',
    opacity: 0,
  },
  '100%': {
    transform: 'translateY(0)',
    opacity: 1,
  },
});

const fadeOut = keyframes({
  '0%': {
    opacity: 1,
  },
  '100%': {
    opacity: 0,
  },
});

const flicker = keyframes({
  to: {
    maskPosition: '50% 50%, 0 50%',
  },
});

const withAnimationImage = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${rotateInVer} 0.375s ease-out both;
        animation-delay: 0.375s;
      `,
  );

const withAnimationTitle = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${trackingInExpanfFwd} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        animation-delay: 0.75s;
      `,
  );

const withAnimationSubtitle = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${slideInTop} 0.3125s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        animation-delay: 1s;
      `,
  );

const withAnimationWrapper = (Component: ElementType) =>
  styled(Component)<{ $activeHideAnimation: boolean }>(
    ({ $activeHideAnimation }) =>
      $activeHideAnimation &&
      css`
        animation: ${fadeOut} 0.625s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
      `,
  );

const withAnimationBackground = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${flicker} 12.5s infinite linear;
      `,
  );

export const StyledImage = withAnimationImage(
  styled.img(({ theme: { size } }) => ({
    width: size.units(22.5),
  })),
);

export const StyledBasicTitle = withAnimationTitle(
  styled(BasicTitle)(({ theme: { color } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
  })),
);

export const StyledBiggerSubtitle = withAnimationSubtitle(
  styled(BiggerSubtitle)(({ theme: { color, size } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
    padding: size.units(1.5),
  })),
);

export const Background = withAnimationBackground(
  styled.div(() => ({
    background:
      'conic-gradient(from 180deg at 50% 70%,hsla(0, 0%, 98%, 1) 0deg,#eec32d 72.0000010728836deg,#ec4b4b 144.0000021457672deg,#709ab9 216.00000858306885deg,#4dffbf 288.0000042915344deg,hsla(0, 0%, 98%, 1) 1turn)',
    width: '100%',
    height: '100%',
    mask: 'radial-gradient(circle at 50% 50%, white 2px, transparent 2.5px) 50% 50% / 20px 20px, url("https://assets.codepen.io/605876/noise-mask.png") 256px 50% / 256px 256px',
    maskComposite: 'intersect',
  })),
);

export const Wrapper = withAnimationWrapper(
  styled.div(({ theme: { size } }) => ({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    [StyledBasicTitle]: {
      marginTop: size.units(3),
    },

    [StyledBiggerSubtitle]: {
      marginTop: size.units(1.5),
    },

    [Background]: {
      position: 'absolute',
    },
  })),
);
